import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { TEXT_DIRECTION } from 'helpers/isRtlLanguage';
import PropTypes from 'prop-types';
import { CDN_TYPE_CMS, imageUrlCDN } from 'utils';

import { Slider } from '@atlas/components';
import {
  getPromotionsList,
  SiteConfigManager,
  useApplicationState,
  useSettings,
} from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';
import { ChipsItem, Label } from '@atlas/ui';

import { BASE_PATH } from 'common/constants';
import { PromotionCard } from 'components/Promotion';
import { useIsRtl } from 'hooks/useIsRtl';

const LobbyPromotionsBlock = ({ blockTitleKey, imageUrl }) => {
  const [isShowImg, setIsShowImg] = useState(true);
  const handleError = () => setIsShowImg(false);

  const isRtl = useIsRtl();
  const dir = useMemo(() => (isRtl ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR), [isRtl]);

  const { authenticated } = useApplicationState();
  const { lang } = useSettings();
  const { push: navigate } = useHistory();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  const promotionsPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.PROMOTIONS);

  useEffect(() => {
    const userVisibilities = ['public', authenticated ? 'registered' : 'anonymous'];

    setLoading(true);
    getPromotionsList(userVisibilities)
      .then(promos => {
        const sorted = (promos || []).sort((a, b) => {
          const aOrder = a.order_position ?? 0;
          const bOrder = b.order_position ?? 0;
          return aOrder - bOrder;
        });
        setPromotions(sorted);
      })
      .catch(err => {
        console.error('Failed to load promotions', err);
        setPromotions([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [lang, authenticated]);

  if (loading || promotions.length === 0) {
    return null;
  }

  return (
    <div className="casino-lobby__item lobby-promotions">
      <div className="casino-lobby__header">
        <div className="casino-lobby__left">
          {isShowImg && (
            <div className="casino-lobby__icon">
              <img
                alt={blockTitleKey}
                height={24}
                onError={handleError}
                role="presentation"
                src={
                  imageUrl
                    ? `${encodeURI(imageUrlCDN(imageUrl, CDN_TYPE_CMS))}`
                    : `${BASE_PATH || ''}/static/assets/images/deposit-bonus.png`
                }
                width={24}
              />
            </div>
          )}
          {blockTitleKey && (
            <div className="casino-lobby__title">
              <Label message={blockTitleKey} />
            </div>
          )}
        </div>
        <div className="casino-lobby__right">
          <ChipsItem
            className="casino__category__view-all"
            label={
              <div className="casino__category__title__view">
                <Label className="casino__category__title__text" message="View_All" />
              </div>
            }
            onClick={() => navigate(promotionsPath)}
          />
        </div>
      </div>

      <div className="casino-lobby__content">
        <div className="lobby-promotions__slider">
          <Slider
            autoplay={{
              delay: 5000 * 100,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            dir={dir}
            direction="horizontal"
            loop={false}
            spaceBetween={8}
            speed={1000}
          >
            {promotions.map((item, index) => (
              <Slider.Slide key={loading ? `skeleton-${index}` : item.id}>
                <PromotionCard
                  buttonSize="small"
                  buttonText={item.button}
                  description={item.description}
                  id={item.id}
                  imageUrl={item.image_url}
                  layout={item.layout}
                  link={item.link}
                />
              </Slider.Slide>
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

LobbyPromotionsBlock.propTypes = {
  blockTitleKey: PropTypes.string,
  imageUrl: PropTypes.string,
};

export default LobbyPromotionsBlock;
